// Recovers from stale-deploy chunk errors.
//
// After a deploy, a client still running the previous build may try to
// lazy-load a code-split chunk whose hashed filename no longer exists on the
// server, producing "Failed to fetch dynamically imported module". When that
// happens we force a single page reload so the browser revalidates index.html
// and picks up the freshly deployed entry + chunks.

const RELOAD_FLAG = 'chunk-reload-attempted';

const CHUNK_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i;

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return CHUNK_ERROR_PATTERN.test(message);
}

// Records a reload attempt and reports whether one should happen. Guarded so a
// genuinely missing/broken chunk (e.g. server outage) cannot trigger a reload
// loop. Kept separate from the navigation side-effect so the decision is
// unit-testable without reloading the page.
export function shouldReloadForNewChunks(): boolean {
  if (sessionStorage.getItem(RELOAD_FLAG)) {
    return false;
  }
  sessionStorage.setItem(RELOAD_FLAG, '1');
  return true;
}

// Reload once to pick up freshly deployed chunks.
export function reloadForNewChunks(): void {
  if (shouldReloadForNewChunks()) {
    window.location.reload();
  }
}

// Clear the guard once the app has navigated successfully, so a future
// deploy in the same session can recover again.
export function clearChunkReloadGuard(): void {
  sessionStorage.removeItem(RELOAD_FLAG);
}

// Catches dynamic-import / CSS preload failures raised by Vite's preload
// helper for component-level lazy imports (router errors are handled
// separately via router.onError).
export function registerChunkReload(): void {
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    reloadForNewChunks();
  });
}
